// src/components/SPanel/PracticeStatistics.jsx
//
// Statistica practicii, din statistica pe întrebări (biletele și temele au
// aceleași întrebări): câte bilete și câte teme sunt trecute, plus acuratețea
// tuturor răspunsurilor date.
// O întrebare e verde de la primul răspuns corect (rămâne verde), roșie dacă
// are doar răspunsuri greșite; un bilet / o temă e trecut(ă) când toate
// întrebările sunt verzi. La acuratețe se numără fiecare răspuns, și cele
// repetate la aceeași întrebare (8 răspunsuri la o întrebare = 8).

import React, { useCallback, useEffect, useState } from "react";
import { getPracticeQuestionStats } from "../../api/examService";
import ProgressSegments from "./ProgressSegments";
import {
   PRACTICE_ATTEMPT_FINISHED_EVENT,
   PRACTICE_TICKET_IDS,
   answersAccuracy,
   groupByState,
} from "../../utils/practiceStatsSummary";

/* ================= i18n ================= */
const I18N = {
   ro: {
      title: "Statistică",
      loading: "Se încarcă…",
      err_load: "Nu am putut încărca statistica.",
      tickets: "Bilete",
      topics: "Teme",
      answers: "Întrebări răspunse",
      passed_of: "trecute",
      legend_passed: "Trecute: {n}",
      legend_errors: "Cu greșeli: {n}",
      legend_started: "În lucru: {n}",
      legend_untouched: "Neîncepute: {n}",
      group_aria: "{ok} trecute, {bad} cu greșeli din {total}",
      empty_topics: "Nu sunt teme.",
      accuracy: "acuratețe",
      answers_total: "Răspunsuri: {n}",
      answers_correct: "Corecte: {n}",
      answers_wrong: "Greșite: {n}",
      answers_aria: "{ok} corecte, {bad} greșite din {total} răspunsuri",
   },
   ru: {
      title: "Статистика",
      loading: "Загрузка…",
      err_load: "Не удалось загрузить статистику.",
      tickets: "Билеты",
      topics: "Темы",
      answers: "Отвеченные вопросы",
      passed_of: "пройдено",
      legend_passed: "Пройдено: {n}",
      legend_errors: "С ошибками: {n}",
      legend_started: "В процессе: {n}",
      legend_untouched: "Не начато: {n}",
      group_aria: "{ok} пройдено, {bad} с ошибками из {total}",
      empty_topics: "Тем нет.",
      accuracy: "точность",
      answers_total: "Ответов: {n}",
      answers_correct: "Верных: {n}",
      answers_wrong: "Ошибок: {n}",
      answers_aria: "{ok} верных, {bad} ошибок из {total} ответов",
   },
};

const formatI18n = (str, vars) =>
   vars ? str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`) : str;

/* ===== limbă partajată (citește din localStorage + ascultă evenimentul "exam:lang") ===== */
function useSharedLang() {
   const [lang, setLang] = useState(() => {
      const saved =
         (typeof localStorage !== "undefined" &&
            localStorage.getItem("exam.lang")) ||
         "ro";
      return saved === "ru" ? "ru" : "ro";
   });

   useEffect(() => {
      const onCustom = (e) => setLang(e.detail === "ru" ? "ru" : "ro");
      const onStorage = (e) => {
         if (e.key === "exam.lang") setLang(e.newValue === "ru" ? "ru" : "ro");
      };
      window.addEventListener("exam:lang", onCustom);
      window.addEventListener("storage", onStorage);
      return () => {
         window.removeEventListener("exam:lang", onCustom);
         window.removeEventListener("storage", onStorage);
      };
   }, []);

   return lang;
}

function StatCard({ title, value, of, unit, bar, children }) {
   return (
      <section className="practice-stats__card">
         <h3 className="practice-stats__card-title">{title}</h3>
         <div className="practice-stats__headline">
            <span className="practice-stats__headline-num">{value}</span>
            {of != null && (
               <span className="practice-stats__headline-of">/ {of}</span>
            )}
            <span className="practice-stats__headline-unit">{unit}</span>
         </div>
         <ProgressSegments className="practice-stats__progress" {...bar} />
         {children}
      </section>
   );
}

export default function PracticeStatistics() {
   const lang = useSharedLang();
   const t = useCallback(
      (key, vars) =>
         formatI18n((I18N[lang] && I18N[lang][key]) || I18N.ro[key] || key, vars),
      [lang],
   );

   const [stats, setStats] = useState(null);
   const [failed, setFailed] = useState(false);
   const [refreshKey, setRefreshKey] = useState(0);

   // după ieșirea dintr-un bilet / o temă statistica se reîncarcă
   useEffect(() => {
      const onFinished = () => setRefreshKey((k) => k + 1);
      window.addEventListener(PRACTICE_ATTEMPT_FINISHED_EVENT, onFinished);
      return () =>
         window.removeEventListener(PRACTICE_ATTEMPT_FINISHED_EVENT, onFinished);
   }, []);

   useEffect(() => {
      let alive = true;
      getPracticeQuestionStats(PRACTICE_TICKET_IDS)
         .then((data) => {
            if (!alive) return;
            setStats(data || null);
            setFailed(false);
         })
         .catch(() => {
            if (alive) setFailed(true);
         });
      return () => {
         alive = false;
      };
   }, [refreshKey]);

   const summary = stats?.summary;
   const tickets = groupByState(stats?.tickets);
   const topics = groupByState(stats?.categories);
   const { correct, wrong, answered, percent } = answersAccuracy(summary);
   const accuracy = percent == null ? "—" : `${percent}%`;

   const groupCard = (title, group, empty) => (
      <StatCard
         title={title}
         value={group.passed}
         of={group.total}
         unit={t("passed_of")}
         bar={{
            green: group.passed,
            red: group.errors,
            total: group.total,
            label: t("group_aria", {
               ok: group.passed,
               bad: group.errors,
               total: group.total,
            }),
         }}
      >
         {group.total === 0 && empty ? (
            <p className="practice-stats__note">{empty}</p>
         ) : (
            <div className="practice-stats__legend">
               <span className="practice-stats__legend-item practice-stats__legend-item--ok">
                  {t("legend_passed", { n: group.passed })}
               </span>
               <span className="practice-stats__legend-item practice-stats__legend-item--bad">
                  {t("legend_errors", { n: group.errors })}
               </span>
               {group.started > 0 && (
                  <span className="practice-stats__legend-item practice-stats__legend-item--started">
                     {t("legend_started", { n: group.started })}
                  </span>
               )}
               <span className="practice-stats__legend-item">
                  {t("legend_untouched", { n: group.untouched })}
               </span>
            </div>
         )}
      </StatCard>
   );

   return (
      <div className="practice-stats">
         <div className="practice-stats__head">
            <h2>{t("title")}</h2>
         </div>

         {!summary && (
            <div className="practice-stats__loading">
               {failed ? t("err_load") : t("loading")}
            </div>
         )}

         {summary && (
            <div className="practice-stats__cards">
               {groupCard(t("tickets"), tickets)}
               {groupCard(t("topics"), topics, t("empty_topics"))}

               <StatCard
                  title={t("answers")}
                  value={accuracy}
                  unit={t("accuracy")}
                  bar={{
                     green: correct,
                     red: wrong,
                     total: answered,
                     label: t("answers_aria", {
                        ok: correct,
                        bad: wrong,
                        total: answered,
                     }),
                  }}
               >
                  <div className="practice-stats__legend">
                     <span className="practice-stats__legend-item practice-stats__legend-item--ok">
                        {t("answers_correct", { n: correct })}
                     </span>
                     <span className="practice-stats__legend-item practice-stats__legend-item--bad">
                        {t("answers_wrong", { n: wrong })}
                     </span>
                     <span className="practice-stats__legend-item practice-stats__legend-item--plain">
                        {t("answers_total", { n: answered })}
                     </span>
                  </div>
               </StatCard>
            </div>
         )}
      </div>
   );
}
